import React, { useState } from 'react';
import { Loader2, X, Copy, Check, Send } from 'lucide-react';

export const AIResponse = ({ content, isLoading, onClose, onAskAI }) => {
  const [copied, setCopied] = useState(false);
  const [newQuery, setNewQuery] = useState('');
  
  if (!content && !isLoading) return null;
  
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(content);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy text:', err);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (newQuery.trim()) {
      onAskAI(newQuery);
      setNewQuery('');
    }
  };

  const clearHistoryAndClose = async () => {
    try {
      await fetch('/api/ai/clear-history', {
        method: 'POST',
      });
      onClose();
    } catch (error) {
      console.error('Error clearing chat history:', error);
      onClose(); // Still close even if clearing history fails
    }
  };

  const isHeading = (line) => {
    return (
      (line.trim().startsWith('**') && line.trim().endsWith('**')) ||
      (line.trim().endsWith(':') && !line.startsWith('-'))
    );
  };

  const shouldAddSpacing = (currentLine, nextLine) => {
    return isHeading(nextLine) || (currentLine.trim().length > 0 && nextLine.trim().length === 0);
  };

  // Process content to remove introductory phrases and text before "---"
  const processContent = (rawContent) => {
    // First split on "---" if it exists
    const parts = rawContent.split('---');
    let content = parts.length > 1 ? parts[1].trim() : rawContent.trim();
    
    // Remove common introductory phrases
    const introPatterns = [
      /^Certainly!?\s*/i,
      /^Here'?s?\s*/i,
      /^Sure!?\s*/i,
      /^Of course!?\s*/i,
      /^I'd be happy to help!?\s*/i,
      /^Let me help!?\s*/i,
      /^Here is\s*/i,
      /^Alright!?\s*/i,
      /^Okay!?\s*/i,
      /^OK!?\s*/i
    ];
    
    for (const pattern of introPatterns) {
      content = content.replace(pattern, '');
    }
    
    return content.trim();
  };

  const processedContent = processContent(content);

  return (
    <div className="fixed inset-x-0 bottom-5 top-[15vh] mx-auto max-w-7xl bg-white rounded-lg shadow-lg z-50">
      <div className="h-full flex flex-col">
        {/* Header */}
        <div className="flex justify-between items-center p-4 border-b">
          <h3 className="text-lg font-semibold text-gray-900">Preface Response</h3>
          <button
            onClick={clearHistoryAndClose}
            className="p-1 hover:bg-gray-100 rounded-full"
          >
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {isLoading ? (
            <div className="flex items-center justify-center h-full">
              <Loader2 className="h-8 w-8 animate-spin text-teal-600" />
            </div>
          ) : (
            <div className="prose max-w-4xl mx-auto">
              {processedContent.split('\n').map((line, i, arr) => (
                <p 
                  key={i} 
                  className={`${
                    isHeading(line) 
                      ? 'font-bold text-gray-900' 
                      : 'text-gray-700'
                  } ${
                    shouldAddSpacing(line, arr[i + 1] || '')
                      ? 'mb-6'
                      : 'mb-1'
                  }`}
                >
                  {line.replace(/\*\*/g, '')}
                </p>
              ))}
            </div>
          )}
        </div>

        {/* Footer with Input, Send, Copy and OK Buttons */}
        {!isLoading && (
          <div className="p-4 border-t flex items-center justify-between">
            <div className="flex-1 mr-4 flex items-center space-x-4">
              <form onSubmit={handleSubmit} className="flex-1 flex items-center">
                <input
                  type="text"
                  value={newQuery}
                  onChange={(e) => setNewQuery(e.target.value)}
                  placeholder="Ask a follow-up question..."
                  className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
              </form>
              <button
                onClick={handleSubmit}
                disabled={!newQuery.trim()}
                className="px-4 py-2 bg-teal-600 text-white rounded-md hover:bg-teal-700 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center space-x-2"
              >
                <Send className="h-4 w-4" />
              </button>
            </div>
            <div className="flex space-x-3">
              <button
                onClick={handleCopy}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors flex items-center space-x-2"
              >
                {copied ? (
                  <>
                    <Check className="h-4 w-4" />
                    <span>Copied!</span>
                  </>
                ) : (
                  <>
                    <Copy className="h-4 w-4" />
                    <span>Copy</span>
                  </>
                )}
              </button>
              <button
                onClick={clearHistoryAndClose}
                className="px-4 py-2 bg-teal-600 text-white rounded-md hover:bg-teal-700 transition-colors"
              >
                OK
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};